import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { 
  User, Bell, Shield, Palette, Zap, 
  Info, Lock, Monitor, AlertTriangle, Save
} from 'lucide-react';
import { toast } from 'sonner';

export default function AccountSettings() {
  const { user } = useContext(AuthContext);
  const [activeId, setActiveId] = useState('profile');
  const isClickScrollingRef = useRef(false);

  const sections = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'autopilot-settings', label: 'Autopilot Settings', icon: Zap },
  ];

  // Active section tracking via IntersectionObserver
  useEffect(() => {
    const observers = [];
    const ids = sections.map((s) => s.id);

    const observer = new IntersectionObserver(
      (entries) => {
        if (isClickScrollingRef.current) return;
        // Find the entry closest to the top that is intersecting
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        // Trigger when section top is near the sticky nav bottom
        rootMargin: '-120px 0px -60% 0px',
        threshold: 0
      }
    );

    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        observer.observe(el);
        observers.push(el);
      }
    });

    return () => observer.disconnect();
  }, []);

  const handleAnchorClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    isClickScrollingRef.current = true;
    setActiveId(id);
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Re-enable scroll observer after smooth scroll completes
    window.setTimeout(() => {
      isClickScrollingRef.current = false;
    }, 800);
  };

  const handleSaveAll = () => {
    toast.success('All settings saved');
  };

  return (
    <div className="container mx-auto p-6 max-w-3xl" data-testid="account-settings-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-[#8A93A6] mb-2">
          <Link to="/app/account" className="hover:text-white">ACCOUNT</Link>
          <span>/</span>
          <span className="text-white">Settings</span>
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
        <p className="text-[#8A93A6]">Manage your account preferences and security</p>
      </div>

      {/* Sticky anchor nav (sits under global navbar h-16) */}
      <div
        className="sticky top-16 z-30 -mx-2 mb-6 px-2 py-2 bg-[#0B0D12]/85 backdrop-blur supports-[backdrop-filter]:bg-[#0B0D12]/70 border-b border-white/5"
        data-testid="settings-anchor-nav"
      >
        <div className="flex flex-wrap items-center gap-1 p-1 rounded-lg bg-white/[0.03] border border-white/5">
          {sections.map((s) => {
            const Icon = s.icon;
            const active = activeId === s.id;
            return (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={(e) => handleAnchorClick(e, s.id)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  active
                    ? 'bg-pink-500/20 text-pink-300 border border-pink-500/30'
                    : 'text-[#8A93A6] hover:text-white hover:bg-white/[0.04] border border-transparent'
                }`}
                data-testid={`settings-anchor-${s.id}`}
                aria-current={active ? 'true' : undefined}
              >
                <Icon className="h-3.5 w-3.5" />
                {s.label}
              </a>
            );
          })}
        </div>
      </div>

      {/* Unified stacked sections */}
      <div className="space-y-8">
        <section id="profile" className="scroll-mt-32">
          <ProfileSection user={user} />
        </section>
        <section id="notifications" className="scroll-mt-32">
          <NotificationsSection />
        </section>
        <section id="security" className="scroll-mt-32">
          <SecuritySection />
        </section>
        <section id="appearance" className="scroll-mt-32">
          <AppearanceSection />
        </section>
        <section id="autopilot-settings" className="scroll-mt-32">
          <AutopilotSettingsSection user={user} />
        </section>
      </div>

      {/* Global Save All Changes */}
      <div className="mt-10 flex justify-end" data-testid="settings-save-all-wrapper">
        <Button
          size="lg"
          onClick={handleSaveAll}
          className="bg-pink-500 hover:bg-pink-600 text-white shadow-lg shadow-pink-500/20"
          data-testid="settings-save-all-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          Save All Changes
        </Button>
      </div>
    </div>
  );
}

// Profile Section
function ProfileSection({ user }) {
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [timezone, setTimezone] = useState('America/New_York');
  const [language, setLanguage] = useState('en');

  const handleSave = () => {
    toast.success('Profile settings saved');
  };

  return (
    <Card className="glass">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Profile
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert className="bg-blue-500/10 border-blue-500/30">
          <Info className="h-4 w-4 text-blue-500" />
          <AlertDescription className="text-blue-400 text-sm">
            This section manages account preferences only — name, email, timezone, and language.
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="glass"
            />
          </div>

          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="glass"
            />
          </div>

          <div className="space-y-2">
            <Label>Timezone</Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger className="glass">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                <SelectItem value="UTC">UTC</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Language (Optional)</Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="glass">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="es">Español</SelectItem>
                <SelectItem value="fr">Français</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} data-testid="save-profile-btn">
            <Save className="h-4 w-4 mr-2" />
            Save Profile
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Notifications Section
function NotificationsSection() {
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [inAppNotifications, setInAppNotifications] = useState(true);
  const [criticalAlerts, setCriticalAlerts] = useState(true);

  const handleSave = () => {
    toast.success('Notifications saved');
  };

  return (
    <Card className="glass">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notifications
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>Email Notifications</Label>
              <p className="text-xs text-[#8A93A6]">Receive updates and alerts via email</p>
            </div>
            <Switch checked={emailNotifications} onCheckedChange={setEmailNotifications} />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>In-App Notifications</Label>
              <p className="text-xs text-[#8A93A6]">Show notifications within the app</p>
            </div>
            <Switch checked={inAppNotifications} onCheckedChange={setInAppNotifications} />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>Critical Alerts</Label>
              <p className="text-xs text-[#8A93A6]">Important system and security notifications</p>
            </div>
            <Switch checked={criticalAlerts} onCheckedChange={setCriticalAlerts} disabled />
          </div>
        </div>

        <Alert className="bg-yellow-500/10 border-yellow-500/30">
          <AlertTriangle className="h-4 w-4 text-yellow-500" />
          <AlertDescription className="text-yellow-400 text-sm">
            <strong>Autopilot Warnings:</strong> Autopilot failure alerts are read-only and always enabled for safety.
          </AlertDescription>
        </Alert>

        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} data-testid="save-notifications-btn">
            <Save className="h-4 w-4 mr-2" />
            Save Notifications
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Security Section
function SecuritySection() {
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleChangePassword = () => {
    toast.info('Password change functionality coming soon');
  };

  const handleSave = () => {
    toast.success('Security preferences saved');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Card className="glass">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Security
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 glass rounded-lg">
            <div className="space-y-1">
              <Label className="text-white">Password</Label>
              <p className="text-xs text-[#8A93A6]">Last changed 30 days ago</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleChangePassword}>
              Change
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 glass rounded-lg">
            <div className="space-y-1">
              <Label className="text-white">Two-Factor Authentication</Label>
              <p className="text-xs text-[#8A93A6]">Add an extra layer of security</p>
            </div>
            <Badge variant="outline">Not Enabled</Badge>
          </div>

          <div className="flex items-center justify-between p-4 glass rounded-lg">
            <div className="space-y-1">
              <Label className="text-white">Active Sessions</Label>
              <p className="text-xs text-[#8A93A6]">Manage your active sessions</p>
            </div>
            <Button variant="outline" size="sm">View</Button>
          </div>

          <div className="flex items-center justify-between p-4 glass rounded-lg">
            <div className="space-y-1">
              <Label className="text-white">Logout All Devices</Label>
              <p className="text-xs text-[#8A93A6]">Sign out from all active sessions</p>
            </div>
            <Button variant="destructive" size="sm">Logout All</Button>
          </div>

          <div className="p-4 glass rounded-lg">
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-white">Logout</Label>
                <p className="text-xs text-[#8A93A6]">Logs you out on this device.</p>
              </div>
              <Button 
                variant="outline" 
                onClick={handleLogout}
                className="w-full sm:w-auto"
              >
                <Lock className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} data-testid="save-security-btn">
            <Save className="h-4 w-4 mr-2" />
            Save Security
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Appearance Section
function AppearanceSection() {
  const [theme, setTheme] = useState('dark');
  const [density, setDensity] = useState('comfortable');
  const [fontSize, setFontSize] = useState('medium');
  const [reducedMotion, setReducedMotion] = useState(false);

  const handleSave = () => {
    toast.success('Appearance saved');
  };

  return (
    <Card className="glass">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Palette className="h-5 w-5" />
          Appearance
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert className="bg-blue-500/10 border-blue-500/30">
          <Info className="h-4 w-4 text-blue-500" />
          <AlertDescription className="text-blue-400 text-sm">
            These settings control app appearance — theme, density, font size, and accessibility.
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Theme</Label>
            <Select value={theme} onValueChange={setTheme}>
              <SelectTrigger className="glass">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dark">Dark</SelectItem>
                <SelectItem value="light">Light</SelectItem>
                <SelectItem value="system">System</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Density</Label>
            <Select value={density} onValueChange={setDensity}>
              <SelectTrigger className="glass">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="compact">Compact</SelectItem>
                <SelectItem value="comfortable">Comfortable</SelectItem>
                <SelectItem value="spacious">Spacious</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Font Size (Accessibility)</Label>
            <Select value={fontSize} onValueChange={setFontSize}>
              <SelectTrigger className="glass">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="small">Small</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="large">Large</SelectItem>
                <SelectItem value="extra-large">Extra Large</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>Reduced Motion</Label>
              <p className="text-xs text-[#8A93A6]">Minimize animations for accessibility</p>
            </div>
            <Switch checked={reducedMotion} onCheckedChange={setReducedMotion} />
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} data-testid="save-appearance-btn">
            <Save className="h-4 w-4 mr-2" />
            Save Appearance
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Autopilot Settings Section
function AutopilotSettingsSection({ user }) {
  const isPro = user?.plan_tier && ['pro', 'creator', 'agency', 'agency_elite'].includes(user.plan_tier);

  const [mode, setMode] = useState('assisted');
  const [killSwitch, setKillSwitch] = useState(true);

  const [stages, setStages] = useState({
    clean: true,
    replay: true,
    clips: true,
    brand: true,
    optimize: true,
    schedule: true,
    publish: false
  });

  const [permissions, setPermissions] = useState({
    recommendations: true,
    assisted: true,
    autopilot: false
  });

  const pipelineStages = [
    { id: 'clean', label: 'Clean', desc: 'Silence + filler removal' },
    { id: 'replay', label: 'Replay', desc: 'Replay editing pass' },
    { id: 'clips', label: 'Clips', desc: 'Generate short-form clips' },
    { id: 'brand', label: 'Brand', desc: 'Apply brand kit' },
    { id: 'optimize', label: 'Optimize', desc: 'Title, hook, thumbnail optimization' },
    { id: 'schedule', label: 'Schedule', desc: 'Queue posts for publishing' },
    { id: 'publish', label: 'Publish', desc: 'Auto-publish to connected platforms' }
  ];

  const modes = [
    { value: 'manual', label: 'Manual', desc: 'You drive every step. Autopilot stays idle.' },
    { value: 'assisted', label: 'Assisted', desc: 'Autopilot recommends, you confirm each action.' },
    { value: 'full', label: 'Full', desc: 'Autopilot executes the pipeline end-to-end.' }
  ];

  const toggleStage = (id) => setStages((s) => ({ ...s, [id]: !s[id] }));
  const togglePerm = (id) => setPermissions((p) => ({ ...p, [id]: !p[id] }));

  return (
    <Card className="glass" data-testid="autopilot-settings-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5" />
          Autopilot Settings
          {isPro && <Badge variant="outline" className="text-[10px] ml-2 border-pink-500/30 text-pink-300">Pro+</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert className="bg-blue-500/10 border-blue-500/30">
          <Info className="h-4 w-4 text-blue-500" />
          <AlertDescription className="text-blue-400 text-sm">
            Configure how Autopilot behaves across your content pipeline.
          </AlertDescription>
        </Alert>

        {!isPro && (
          <Alert className="bg-pink-500/10 border-pink-500/30">
            <Info className="h-4 w-4 text-pink-500" />
            <AlertDescription className="text-pink-400 text-sm">
              Autopilot is a Pro+ feature. Upgrade to unlock automated processing and streak protection.
            </AlertDescription>
          </Alert>
        )}

        {/* Global Kill Switch — prominent */}
        <div
          className={`rounded-xl border p-4 ${
            killSwitch
              ? 'border-white/10 bg-white/[0.02]'
              : 'border-red-500/40 bg-red-500/[0.06]'
          }`}
          data-testid="autopilot-kill-switch"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1 min-w-0">
              <Label className="text-white flex items-center gap-2 text-base">
                <AlertTriangle className={`h-4 w-4 ${killSwitch ? 'text-yellow-400' : 'text-red-400'}`} />
                Global Kill Switch
                <Badge variant="outline" className="text-[10px] border-white/15 text-[#8A93A6] uppercase tracking-wide">
                  Emergency Stop
                </Badge>
              </Label>
              <p className="text-xs text-[#8A93A6]">
                {killSwitch
                  ? 'Autopilot is enabled and may execute according to mode + permissions.'
                  : 'All automation halted. No autopilot or assisted actions will run.'}
              </p>
            </div>
            <Switch
              checked={killSwitch}
              onCheckedChange={setKillSwitch}
              disabled={!isPro}
              data-testid="autopilot-kill-switch-toggle"
            />
          </div>
        </div>

        {/* Autopilot Mode */}
        <div className="space-y-3" data-testid="autopilot-mode-section">
          <div>
            <h4 className="text-sm font-semibold text-white">Autopilot Mode</h4>
            <p className="text-xs text-[#8A93A6]">Choose how much Autopilot drives the pipeline.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {modes.map((m) => {
              const active = mode === m.value;
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMode(m.value)}
                  disabled={!isPro}
                  className={`text-left rounded-lg border p-3 transition-colors ${
                    active
                      ? 'border-pink-500/40 bg-pink-500/[0.06]'
                      : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.04]'
                  } ${!isPro ? 'opacity-60 cursor-not-allowed' : ''}`}
                  data-testid={`autopilot-mode-${m.value}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-sm font-semibold ${active ? 'text-pink-300' : 'text-white'}`}>
                      {m.label}
                    </span>
                    {active && (
                      <Badge variant="outline" className="text-[9px] border-pink-500/30 text-pink-300 uppercase tracking-wide">
                        Active
                      </Badge>
                    )}
                  </div>
                  <p className="text-[11px] text-[#8A93A6] leading-snug">{m.desc}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Pipeline Stage Toggles */}
        <div className="space-y-3" data-testid="autopilot-stages-section">
          <div>
            <h4 className="text-sm font-semibold text-white">Pipeline Stage Toggles</h4>
            <p className="text-xs text-[#8A93A6]">Enable or disable individual stages of the autopilot pipeline.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {pipelineStages.map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2"
                data-testid={`autopilot-stage-${s.id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm text-white">{s.label}</p>
                  <p className="text-[11px] text-[#8A93A6] truncate">{s.desc}</p>
                </div>
                <Switch
                  checked={stages[s.id]}
                  onCheckedChange={() => toggleStage(s.id)}
                  disabled={!isPro || !killSwitch}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Execution Permissions */}
        <div className="space-y-3" data-testid="autopilot-permissions-section">
          <div>
            <h4 className="text-sm font-semibold text-white">Execution Permissions</h4>
            <p className="text-xs text-[#8A93A6]">Control what level of action Autopilot is allowed to take.</p>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2.5">
              <div className="min-w-0">
                <p className="text-sm text-white">Allow recommendations</p>
                <p className="text-[11px] text-[#8A93A6]">Surface suggestions for the user to review.</p>
              </div>
              <Switch
                checked={permissions.recommendations}
                onCheckedChange={() => togglePerm('recommendations')}
                disabled={!isPro || !killSwitch}
                data-testid="autopilot-perm-recommendations"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2.5">
              <div className="min-w-0">
                <p className="text-sm text-white">Allow assisted actions</p>
                <p className="text-[11px] text-[#8A93A6]">Autopilot performs steps that the user confirms.</p>
              </div>
              <Switch
                checked={permissions.assisted}
                onCheckedChange={() => togglePerm('assisted')}
                disabled={!isPro || !killSwitch}
                data-testid="autopilot-perm-assisted"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2.5">
              <div className="min-w-0">
                <p className="text-sm text-white">Allow autopilot actions</p>
                <p className="text-[11px] text-[#8A93A6]">Autopilot performs steps end-to-end without confirmation.</p>
              </div>
              <Switch
                checked={permissions.autopilot}
                onCheckedChange={() => togglePerm('autopilot')}
                disabled={!isPro || !killSwitch}
                data-testid="autopilot-perm-autopilot"
              />
            </div>
          </div>
        </div>

        {/* Streak Protection */}
        <div className="space-y-3" data-testid="streak-protection-section">
          <div>
            <h4 className="text-sm font-semibold text-white flex items-center gap-2">
              Streak Protection
              <Badge variant="outline" className="text-[10px] border-orange-500/30 text-orange-300 uppercase tracking-wide">
                Recovery Flow
              </Badge>
            </h4>
            <p className="text-xs text-[#8A93A6]">Keeps your streak alive when scheduled content is missing.</p>
          </div>
          <div className="rounded-lg border border-orange-500/20 bg-orange-500/[0.04] p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-orange-300 mb-1">Trigger</p>
                <p className="text-white">No scheduled content</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-orange-300 mb-1">Source</p>
                <p className="text-white">Archived content</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-orange-300 mb-1">Action</p>
                <p className="text-white">Re-optimize &rarr; Schedule &rarr; Publish</p>
              </div>
            </div>
            <p className="text-[11px] text-[#8A93A6] mt-3 leading-relaxed">
              If your queue is empty for the day, Streak Protection pulls eligible archived content, re-optimizes it for current platform signals, and publishes through your active autopilot flow.
            </p>
          </div>
        </div>

        <Alert className="bg-blue-500/10 border-blue-500/30">
          <Monitor className="h-4 w-4 text-blue-500" />
          <AlertDescription className="text-blue-400 text-sm">
            Autopilot settings control how your content workflow behaves.
          </AlertDescription>
        </Alert>

        <div className="flex justify-end pt-2">
          <Button onClick={() => toast.success('Autopilot settings saved')} data-testid="save-autopilot-btn">
            <Save className="h-4 w-4 mr-2" />
            Save Autopilot Settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
